import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import {
  applyChoice,
  readChoice,
  resolve,
  watchSystem,
  type ThemeChoice,
} from '../lib/theme.js';

/**
 * ปุ่มสลับธีม สว่าง → มืด → ตามเครื่อง → วนกลับ
 *
 * ⚠ เป็นปุ่มเดียวสามสถานะ ไม่ใช่สวิตช์สองสถานะ — เหตุผลอยู่ใน lib/theme.ts
 * ตัดสถานะ "ตามเครื่อง" ทิ้งเมื่อไร ผู้ใช้ที่ตั้งเครื่องให้สลับตามเวลาจะถูกล็อก
 * ไว้ที่ธีมเดียวตั้งแต่กดปุ่มครั้งแรก
 *
 * ⚠ ไอคอนบอก "สถานะปัจจุบัน" ไม่ใช่ "สิ่งที่จะได้เมื่อกด" — ตรงข้ามกับปุ่มสลับทิศผัง
 * ข้าง ๆ ที่บอกปลายทาง ที่ต่างกันเพราะทิศของผังผู้ใช้เห็นจากตัวผังอยู่แล้ว
 * แต่ "ตอนนี้ตามเครื่องอยู่ หรือถูกตั้งค่าไว้เอง" มองจากสีหน้าจอไม่ออก
 * ปุ่มจึงต้องเป็นที่เดียวที่บอก และ data-tip ต้องบอกทั้งสถานะนี้และปลายทางถัดไป
 */

const ORDER: ThemeChoice[] = ['light', 'dark', 'system'];

const LABEL: Record<ThemeChoice, string> = {
  light: 'สว่าง',
  dark: 'มืด',
  system: 'ตามเครื่อง',
};

export function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(readChoice);

  useEffect(() => {
    applyChoice(choice);
  }, [choice]);

  /*
   * อยู่โหมด 'ตามเครื่อง' แล้วผู้ใช้ไปสลับธีมที่ระบบปฏิบัติการ (หรือถึงเวลาที่ตั้ง
   * ให้สลับอัตโนมัติ) หน้าจอต้องเปลี่ยนตามทันทีโดยไม่ต้องรีเฟรช
   * โหมด light/dark ไม่ต้องฟัง เพราะผู้ใช้เลือกทับไว้แล้ว
   */
  useEffect(() => {
    if (choice !== 'system') return;
    return watchSystem(() => applyChoice('system'));
  }, [choice]);

  const next = useCallback(() => {
    setChoice((c) => ORDER[(ORDER.indexOf(c) + 1) % ORDER.length] ?? 'system');
  }, []);

  const shown = resolve(choice);
  const upcoming = ORDER[(ORDER.indexOf(choice) + 1) % ORDER.length] ?? 'system';

  return (
    <button
      type="button"
      className="icobtn"
      onClick={next}
      aria-label={`ธีม: ${LABEL[choice]} — กดเพื่อเปลี่ยนเป็น ${LABEL[upcoming]}`}
      data-tip={`ธีม ${LABEL[choice]} → ${LABEL[upcoming]}`}
    >
      {choice === 'system' ? (
        <DesktopOutlined />
      ) : shown === 'dark' ? (
        <MoonOutlined />
      ) : (
        <SunOutlined />
      )}
    </button>
  );
}
